/**
 * @file React hook — useMaterialAnalysisForm.ts: form/state logic for contributor dashboard.
 */

import { useState, useMemo } from "react";
import { isAgnosticSubMaterialSelection } from "../materialAnalysisConstants";
import {
  MaterialPropertyKey,
  NO_MATERIAL_PROPERTY_VALUE,
} from "../materialAnalysisPropertyConfig";

/** Type definition — Material Property Selection. */
export type MaterialPropertySelection = MaterialPropertyKey | typeof NO_MATERIAL_PROPERTY_VALUE;

/** Type definition — Geo Spec. */
export type GeoSpec = "global" | "india" | "state";

/** Type definition — Form Values. */
export interface FormValues {
  materialId: number | null;
  subMaterialId: number | null;
  subMaterialIsAgnostic: boolean;
  materialPropertyKey: MaterialPropertySelection;
  materialPropertyMin: string;
  materialPropertyMax: string;
  temporalRange: [number, number];
  geoSpec: GeoSpec;
  geoSelection: string;
  manufacturerId: number | null;
  technologyId: number | null;
  dataReview: string;
  dataCompleteness: string;
  dataAcquisition: string;
}

const MIN_YEAR = 1990;
const MAX_YEAR = 2030;
const DEFAULT_TEMPORAL_RANGE: [number, number] = [2000, 2024];

const toNumberOrNull = (value: string): number | null => {
  if (value === "" || value === undefined || value === null) return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

/** Hook: use Material Analysis Form. */
export const useMaterialAnalysisForm = () => {
  const [formValues, setFormValues] = useState<FormValues>({
    materialId: null,
    subMaterialId: null,
    subMaterialIsAgnostic: true,
    materialPropertyKey: NO_MATERIAL_PROPERTY_VALUE,
    materialPropertyMin: "",
    materialPropertyMax: "",
    temporalRange: DEFAULT_TEMPORAL_RANGE,
    geoSpec: "global",
    geoSelection: "global",
    manufacturerId: null,
    technologyId: null,
    dataReview: "",
    dataCompleteness: "",
    dataAcquisition: "",
  });

  const applyTemporalAdjustment = (
    mutator: (start: number, end: number) => [number, number]
  ) => {
    setFormValues((prev) => {
      const [currentStart, currentEnd] = prev.temporalRange;
      const width = Math.max(1, currentEnd - currentStart);
      let [rawStart, rawEnd] = mutator(currentStart, currentEnd);
      if (rawStart > rawEnd) {
        [rawStart, rawEnd] = [rawEnd, rawStart];
      }

      let start = rawStart;
      let end = rawEnd;

      if (start < MIN_YEAR) {
        const diff = MIN_YEAR - start;
        start = MIN_YEAR;
        end = Math.min(MAX_YEAR, end + diff);
      }

      if (end > MAX_YEAR) {
        const diff = end - MAX_YEAR;
        end = MAX_YEAR;
        start = Math.max(MIN_YEAR, start - diff);
      }

      if (end - start < width) {
        end = Math.min(MAX_YEAR, start + width);
        if (end - start < width) {
          start = Math.max(MIN_YEAR, end - width);
        }
      }

      if (end <= start) {
        end = Math.min(MAX_YEAR, start + 1);
      }

      return {
        ...prev,
        temporalRange: [Math.round(start), Math.round(end)],
      };
    });
  };

  const shiftTemporalRange = (delta: number) => {
    applyTemporalAdjustment((start, end) => [start + delta, end + delta]);
  };

  const expandTemporalRange = (delta: number) => {
    applyTemporalAdjustment((start, end) => [start - delta, end + delta]);
  };

  const resetTemporalRange = () => {
    setFormValues((prev) => ({
      ...prev,
      temporalRange: DEFAULT_TEMPORAL_RANGE,
    }));
  };

  const resetFilters = () => {
    setFormValues((prev) => ({
      ...prev,
      temporalRange: DEFAULT_TEMPORAL_RANGE,
      geoSpec: "global" as GeoSpec,
      geoSelection: "global",
      manufacturerId: null,
      technologyId: null,
    }));
  };

  const handleTemporalChange = (values: number[]) => {
    const sorted: [number, number] = [
      Math.min(values[0], values[1]),
      Math.max(values[0], values[1]),
    ];
    setFormValues((prev) => ({
      ...prev,
      temporalRange: sorted,
    }));
  };

  const handleGeoChange = (next: GeoSpec) => {
    setFormValues((prev) => ({
      ...prev,
      geoSpec: next,
      geoSelection:
        next === "global" ? "global" : next === "india" ? "India" : "",
    }));
  };

  const handleGeoSelectionChange = (value: string) => {
    setFormValues((prev) => ({
      ...prev,
      geoSelection: value,
    }));
  };

  const handleMaterialChange = (value: string) => {
    setFormValues((prev) => ({
      ...prev,
      materialId: toNumberOrNull(value),
      subMaterialId: null,
      subMaterialIsAgnostic: true,
      materialPropertyKey: NO_MATERIAL_PROPERTY_VALUE,
      materialPropertyMin: "",
      materialPropertyMax: "",
      manufacturerId: null,
      technologyId: null,
    }));
  };

  const handleSubMaterialChange = (value: string) => {
    if (!value || isAgnosticSubMaterialSelection(value)) {
      setFormValues((prev) => ({
        ...prev,
        subMaterialId: null,
        subMaterialIsAgnostic: true,
      }));
      return;
    }

    setFormValues((prev) => ({
      ...prev,
      subMaterialId: toNumberOrNull(value),
      subMaterialIsAgnostic: false,
    }));
  };

  const handleMaterialPropertyKeyChange = (value: string) => {
    if (!value) {
      setFormValues((prev) => ({
        ...prev,
        materialPropertyKey: NO_MATERIAL_PROPERTY_VALUE,
        materialPropertyMin: "",
        materialPropertyMax: "",
      }));
      return;
    }

    setFormValues((prev) => ({
      ...prev,
      materialPropertyKey: value as MaterialPropertyKey,
      materialPropertyMin: "",
      materialPropertyMax: "",
    }));
  };

  const handleMaterialPropertyMinChange = (value: string) => {
    setFormValues((prev) => ({
      ...prev,
      materialPropertyMin: value,
    }));
  };

  const handleMaterialPropertyMaxChange = (value: string) => {
    setFormValues((prev) => ({
      ...prev,
      materialPropertyMax: value,
    }));
  };

  const handleManufacturerChange = (value: string) => {
    setFormValues((prev) => ({
      ...prev,
      manufacturerId: toNumberOrNull(value),
    }));
  };

  const handleTechnologyChange = (value: string) => {
    setFormValues((prev) => ({
      ...prev,
      technologyId: toNumberOrNull(value),
    }));
  };

  const handleReliabilityChange = (
    field: "dataReview" | "dataCompleteness" | "dataAcquisition",
    value: string
  ) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const temporalSummaryLabel = useMemo(
    () => `Data between ${formValues.temporalRange[0]} and ${formValues.temporalRange[1]}`,
    [formValues.temporalRange]
  );

  const geoSummaryLabel = useMemo(() => {
    if (formValues.geoSpec === "global") return "Global Coverage";
    if (formValues.geoSpec === "india") return "India";
    if (formValues.geoSpec === "state") return formValues.geoSelection || "Select State";
    return "Coverage";
  }, [formValues.geoSpec, formValues.geoSelection]);

  return {
    formValues,
    setFormValues,
    MIN_YEAR,
    MAX_YEAR,
    shiftTemporalRange,
    expandTemporalRange,
    resetTemporalRange,
    resetFilters,
    handleTemporalChange,
    handleGeoChange,
    handleGeoSelectionChange,
    handleMaterialChange,
    handleSubMaterialChange,
    handleMaterialPropertyKeyChange,
    handleMaterialPropertyMinChange,
    handleMaterialPropertyMaxChange,
    handleManufacturerChange,
    handleTechnologyChange,
    handleReliabilityChange,
    temporalSummaryLabel,
    geoSummaryLabel,
  };
};

